import { useNavigate } from 'react-router-dom'

import { Wizard } from '@factorialco/deprecated-design-system'
import {
  AssignBudgetToTrainingClassDocument,
  BudgetLegalEntityFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { track } from 'modules/core/lib/Tracking'

import { useCreateBudget } from 'modules/finance/hooks/useBudgets'
import { events } from 'modules/finance/lib/events'

import { allocationStep } from './AllocationStep'
import { amountsAndDatesStep } from './AmountsAndDatesStep'
import { basicDetailsStep } from './BasicDetailsStep'
import { TrainingsWizardData } from './lib/trainingsBudgetWizard'

interface Props {
  onSuccess: (budgetId: number) => void
  onError: () => void
  legalEntities: BudgetLegalEntityFragment[]
}

export const TrainingsBudgetWizard = ({ onSuccess, onError, legalEntities }: Props) => {
  const session = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { createBudget } = useCreateBudget()
  const [assignBudgetToTrainingClass] = useMutation(AssignBudgetToTrainingClassDocument, {
    extractorFn: (data) => data.trainingsMutations.updateTrainingsTrainingClass,
  })

  const mainLegalEntity = legalEntities?.find((node) => node.main == true)

  const initialData: TrainingsWizardData = {
    legalEntityId: mainLegalEntity?.id,
    legalEntities: legalEntities,
    success: false,
    name: '',
    description: '',
    effectiveFrom: '',
    effectiveUntil: undefined,
    amountCents: '',
    currency: mainLegalEntity?.currency ?? '',
    errors: {},
    selectedTrainingIds: [],
    selectedTrainingClassIds: [],
  }

  const handleSubmit = async (data: TrainingsWizardData) => {
    if (!data.legalEntityId) {
      return Promise.reject()
    }

    const budgetAmount = Number(data.amountCents)

    // Create budget with training class budgetable type
    const response = await createBudget({
      name: data.name,
      description: data.description,
      legalEntityId: data.legalEntityId,
      amountCents: toCents(budgetAmount),
      effectiveFrom: data.effectiveFrom,
      effectiveUntil: data.effectiveUntil,
      periodicity: 'none',
      ownerEmployeeIds: [session.employee.id],
      viewerEmployeeIds: [],
      spenderEmployeeIds: [],
      budgetableBy: 'training_class',
    })

    const result = handleResult(response, onError)

    if (!result.success) {
      onError()
      return
    }

    const budgetId = result.data.budget?.id

    track(events.completeAddBudget, { budgetId, type: 'trainings' })

    // Assign selected training classes to the new budget
    if (budgetId && data.selectedTrainingClassIds.length > 0) {
      await Promise.all(
        data.selectedTrainingClassIds.map((classId) =>
          assignBudgetToTrainingClass({
            variables: {
              id: Number(classId),
              budgetId,
            },
          })
        )
      )
    }

    if (budgetId) {
      onSuccess(budgetId)
    } else {
      onError()
    }
  }

  const steps = [basicDetailsStep(), amountsAndDatesStep(), allocationStep()]

  return (
    <Wizard.Modal
      size="large"
      onClose={() => {
        navigate(navigation.expenses.budgets.fullPath())
      }}
      title={i18n.t('budgets.forms.trainings_budget.title')}
      icon="money-bag"
      steps={steps}
      initialData={initialData}
      onSubmit={handleSubmit}
    />
  )
}
